import { useState, ChangeEvent } from "react";
import { useSelectFilter } from "../../../hooks/useSelectFilter";
import { useDomains } from "./useDomains";
import { Domain } from "../models/Domain";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { useStoredDomain } from "./useStoredDomain";

export interface UseDomainFilterReturn {
  selectedDomainId: string;
  setSelectedDomainId: (val: string) => void;
  handleDomainChange: (val: string) => void;
  options: { label: string; value: string }[];
  loading: boolean;
  searchInput: string;
  handleSearchInputChange: (val: string) => void;
}

export const useDomainFilter = (
  initial: string | "all" = "all",
  onChange?: () => void
): UseDomainFilterReturn => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, { delay: 300 });
  const selectedDomain = useStoredDomain();

  const {
    fetch: { data: domains = [], loading },
  } = useDomains({ params: { page: 1, search: debouncedSearch } });

  const {
    value: selectedDomainId,
    handleChange: handleDomainChange,
    setValue: setSelectedDomainId,
  } = useSelectFilter<string>(initial, onChange);

  const options = !selectedDomain
    ? [
        { label: "All Domains", value: "all" },
        ...domains.map((d: Domain) => ({
          label: `${d.id}: ${d.name} - ${d.domain_url}`,
          value: d.id,
        })),
      ]
    : [
        {
          label: `${selectedDomain.id}: ${selectedDomain.name} - ${selectedDomain.domain_url}`,
          value: selectedDomain.id,
        },
      ];

  const handleSearchInputChange = (val: string) => {
    setSearchInput(val);
  };

  return {
    selectedDomainId: selectedDomain ? selectedDomain.id : selectedDomainId,
    setSelectedDomainId,
    handleDomainChange,
    options,
    loading,
    searchInput,
    handleSearchInputChange,
  };
};
